import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  FaArrowLeft, 
  FaArrowRight, 
  FaTimes, 
  FaCalendarAlt, 
  FaFilter, 
  FaSearch, 
  FaFileExcel, 
  FaCalendarDay, 
  FaCalendarWeek, 
  FaListUl, 
  FaCalendarCheck,
  FaInfoCircle,
  FaUser,
  FaChartBar,
  FaChevronDown,
  FaChevronUp,
  FaTrophy,
  FaMedal,
  FaStar,
  FaClipboardList,
  FaCheckCircle,
  FaHourglassHalf,
  FaUserTie,
  FaProjectDiagram,
  FaSortUp,
  FaSortDown,
  FaSort,
  FaCheck,
  FaUmbrellaBeach
} from 'react-icons/fa';
import { collection, query, getDocs, where, doc, getDoc, addDoc, deleteDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import 'bootstrap/dist/css/bootstrap.min.css';
import styles from './OperationManagementPage.module.css';
import { deleteVisitMirrors, isMirrorVisit } from '../../utils/hybridVisitMirror';
import { mergeByTime, timeToMinutes, getVisitTime, getJobTime } from '../../utils/calendarSorting';
import VisitModal from './VisitModal';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  COMPLETION_STATE,
  getCompletionState,
  getCompletionTooltipLine,
  formatCompletionDate,
  formatCompletionDateShort,
  getPendingCompletionLabels,
} from '../../utils/visitCompletion';
import { normalizePatientId } from '../../utils/patientAccess';
import { toast } from 'react-toastify';
import './OperationManagementDarkMode.css';
import SCTrackingTable from './SCTrackingTable';
import useAllowedEmployeeIds from '../../hooks/useAllowedEmployeeIds';
import {
  fetchEmployeeLeaves,
  buildLeaveDayMap,
  getLeavesForDate,
  describeLeaves,
} from '../../utils/employeeLeaves';

const OperationManagementPage = () => {
  const { employeeId, userEmail, userAuthorization } = useUserProfile();
  const { isDarkMode } = useTheme();
  const calendarRef = useRef(null);

  // Check if user is a restricted user (can only view and delete)
  const isRestrictedUser = userEmail === 'turanemre1999@gmail.com';

  // Çalışan Filtresi visibility tiers:
  //  - admin            → all employees (no restriction)
  //  - SMO (owns centers, like in SMO Lead Reports) → only their workers
  //  - Region Manager (manages regions) → workers in their region's centers
  //  - everyone else    → only themselves
  // userAuthorization may be an array or a single string — normalize to an array.
  const authList = Array.isArray(userAuthorization)
    ? userAuthorization
    : (userAuthorization ? [userAuthorization] : []);
  const isAdmin = authList.includes('admin');

  // Set of employee IDs the current user is allowed to filter by.
  // null = no restriction (admin sees everyone).
  const allowedEmployeeIds = useAllowedEmployeeIds(isAdmin, employeeId);

  // Dropdown-related states
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');

  const [currentDate, setCurrentDate] = useState(new Date());
  const [visits, setVisits] = useState([]);
  const [calendarJobs, setCalendarJobs] = useState([]);
  // Seçili çalışanın onaylanmış izinleri: 'yyyy-mm-dd' → izin kayıtları
  const [leaveDayMap, setLeaveDayMap] = useState(() => new Map());
  const [isLoading, setIsLoading] = useState(false);

  // "All employees" dataset — lazy loaded only when Personnel Reports is
  // expanded or when generating a detailed report. Kept separate from the
  // per-employee `visits`/`calendarJobs` so the calendar stays lean.
  const [allVisits, setAllVisits] = useState([]);
  const [allCalendarJobs, setAllCalendarJobs] = useState([]);
  const [isLoadingAllData, setIsLoadingAllData] = useState(false);
  const [allDataLoaded, setAllDataLoaded] = useState(false);

  const [selectedVisit, setSelectedVisit] = useState(null);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDeleteModal, setShowJobDeleteModal] = useState(false);
  const [isDeletingJob, setIsDeletingJob] = useState(false);

  // Cascade delete ("this one and everything after it") — admin only.
  // `cascade` holds the anchor item plus the full list of documents that the
  // confirm step will remove, so the preview and the delete can never diverge.
  const [cascade, setCascade] = useState(null);
  const [isBuildingCascade, setIsBuildingCascade] = useState(false);
  const [isCascadeDeleting, setIsCascadeDeleting] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [viewMode, setViewMode] = useState('month');
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  // Personnel Reports Section State
  const [showPersonnelReports, setShowPersonnelReports] = useState(false);
  const [reportPeriod, setReportPeriod] = useState('month'); // 'week', 'month', 'year', 'custom'
  const [customReportStartDate, setCustomReportStartDate] = useState('');
  const [customReportEndDate, setCustomReportEndDate] = useState('');
  const [sortField, setSortField] = useState('totalVisits'); // Default sort by total visits
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'
  const [selectedEmployeeStats, setSelectedEmployeeStats] = useState(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);

  // Column filter states for Personnel Reports table
  const [reportFilters, setReportFilters] = useState({
    employeeName: '',
    totalVisits: '',
    completedVisits: '',
    totalJobs: '',
    completedJobs: '',
    projectsWorkedOn: '',
    completionRate: ''
  });

  /**
   * 1. Fetch employees and projects from Firestore
   */
  useEffect(() => {
    const fetchEmployeesAndProjects = async () => {
      try {
        // Fetch employees
        const employeesSnapshot = await getDocs(collection(db, 'Employees'));
        const employeesData = employeesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setEmployees(employeesData);

        // Fetch projects
        const projectsSnapshot = await getDocs(collection(db, 'Projects'));
        const projectsData = projectsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setProjects(projectsData);
      } catch (error) {
        console.error('Error fetching employees and projects:', error);
        toast.error('Veriler yüklenirken hata oluştu');
      }
    };

    fetchEmployeesAndProjects();
  }, []);

  /**
   * Helper function to get date range for a month with buffer
   */
  const getMonthDateRange = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();

    // Start from 7 days before the month starts
    const startDate = new Date(year, month, -7);
    // End 7 days after the month ends
    const endDate = new Date(year, month + 1, 7);

    return { startDate, endDate };
  };

  /**
   * 2. Fetch visits and jobs for specific month or all visits
   */
  const fetchDataForMonth = async (date, employeeId) => {
    // Lazy loading: do not fetch visits/jobs until an employee filter is picked.
    // Avoids pulling the entire Visits + CalendarJobs collections on initial load.
    if (!employeeId) {
      setVisits([]);
      setCalendarJobs([]);
      setLeaveDayMap(new Map());
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Fetch visits for the selected employee (matches SMO Lead Reports behavior)
      const visitsQuery = query(
        collection(db, 'Visits'),
        where('employeeId', '==', employeeId)
      );
      const visitsSnapshot = await getDocs(visitsQuery);
      const visitsData = visitsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch calendar jobs for the selected employee
      const jobsQueryRef = query(
        collection(db, 'CalendarJobs'),
        where('employeeId', '==', employeeId)
      );
      const jobsSnapshot = await getDocs(jobsQueryRef);
      const jobsData = jobsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      setVisits(visitsData);
      setCalendarJobs(jobsData);

      // Onaylanmış izinler (İzin Yönetimi kayıtları + onaylanan talepler).
      // Takvimde vizit/iş olmasa bile çalışanın izinli günleri görünür.
      try {
        const leaves = await fetchEmployeeLeaves(employeeId);
        setLeaveDayMap(buildLeaveDayMap(leaves));
      } catch (leaveError) {
        console.error('Error fetching leaves:', leaveError);
        setLeaveDayMap(new Map());
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Veriler yüklenirken hata oluştu');
      setIsLoading(false);
    }
  };

  /**
   * Fetch data when month or employee filter changes
   * When employee is selected, shows all visits/jobs (matches SMO Lead Reports)
   * When no employee selected, shows month-filtered data for calendar view
   */
  useEffect(() => {
    fetchDataForMonth(currentDate, selectedEmployeeId);
  }, [currentDate, selectedEmployeeId]);

  /**
   * Fetch the full Visits + CalendarJobs collections across all employees.
   * Used by Personnel Reports and the detailed Excel report.
   */
  const fetchAllVisitsAndJobs = useCallback(async ({ force = false } = {}) => {
    try {
      setIsLoadingAllData(true);
      const [visitsSnapshot, jobsSnapshot] = await Promise.all([
        getDocs(collection(db, 'Visits')),
        getDocs(collection(db, 'CalendarJobs'))
      ]);
      const freshVisits = visitsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      const freshJobs = jobsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAllVisits(freshVisits);
      setAllCalendarJobs(freshJobs);
      setAllDataLoaded(true);
      return { visits: freshVisits, jobs: freshJobs };
    } catch (error) {
      console.error('Error fetching all visits/jobs:', error);
      toast.error('Tüm veriler yüklenirken hata oluştu');
      return { visits: [], jobs: [] };
    } finally {
      setIsLoadingAllData(false);
    }
  }, []);

  // Auto-load the full dataset when Personnel Reports is expanded.
  useEffect(() => {
    if (showPersonnelReports && !allDataLoaded && !isLoadingAllData) {
      fetchAllVisitsAndJobs();
    }
  }, [showPersonnelReports, allDataLoaded, isLoadingAllData, fetchAllVisitsAndJobs]);

  // Apply dark mode class to document body
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [isDarkMode]);

  // Helper function (stable reference)
  const normalizeDate = useCallback((date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }, []);

  // Memoize employee and project lookup maps for O(1) access instead of O(n) find()
  const employeeMap = useMemo(() => {
    const map = new Map();
    employees.forEach(emp => map.set(emp.id, emp));
    return map;
  }, [employees]);

  const projectMap = useMemo(() => {
    const map = new Map();
    projects.forEach(proj => map.set(proj.id, proj));
    return map;
  }, [projects]);

  // Employees shown in the Çalışan Filtresi dropdown, scoped to what the
  // current user is allowed to see (admin → all, SMO Lead → their workers,
  // Region Manager → workers in their region, everyone else → only themselves).
  const filterableEmployees = useMemo(() => {
    // While a restricted user's allowed set is still being computed, show
    // nothing rather than briefly exposing every employee.
    if (!isAdmin && !allowedEmployeeIds) return [];
    return employees
      .filter(emp => emp.Active === 1)
      .filter(emp => !allowedEmployeeIds || allowedEmployeeIds.has(emp.id))
      .sort((a, b) =>
        `${a.eName} ${a.eSurname}`.localeCompare(
          `${b.eName} ${b.eSurname}`, 'tr', { sensitivity: 'base' }
        )
      );
  }, [employees, allowedEmployeeIds, isAdmin]);

  // Personnel Statistics Calculation
  const personnelStatistics = useMemo(() => {
    const now = new Date();
    let filterStartDate, filterEndDate;

    switch (reportPeriod) {
      case 'week':
        filterStartDate = new Date(now);
        filterStartDate.setDate(now.getDate() - now.getDay() + 1); // Monday
        filterEndDate = new Date(filterStartDate);
        filterEndDate.setDate(filterStartDate.getDate() + 6); // Sunday
        break;
      case 'month':
        filterStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
        filterEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'year':
        filterStartDate = new Date(now.getFullYear(), 0, 1);
        filterEndDate = new Date(now.getFullYear(), 11, 31);
        break;
      case 'custom':
        filterStartDate = customReportStartDate ? new Date(customReportStartDate) : new Date(now.getFullYear(), now.getMonth(), 1);
        filterEndDate = customReportEndDate ? new Date(customReportEndDate) : new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      default:
        filterStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
        filterEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    }

    filterStartDate.setHours(0, 0, 0, 0);
    filterEndDate.setHours(23, 59, 59, 999);

    const statsMap = new Map();

    // Initialize stats for all active employees
    employees.filter(emp => emp.Active === 1).forEach(emp => {
      statsMap.set(emp.id, {
        employeeId: emp.id,
        employeeName: `${emp.eName} ${emp.eSurname}`,
        totalVisits: 0,
        completedVisits: 0,
        inProgressVisits: 0,
        totalJobs: 0,
        completedJobs: 0,
        projectsWorkedOn: new Set(),
        totalVisitDays: 0,
        averageVisitDuration: 0,
        visitTypes: {}
      });
    });

    // Process visits (uses full dataset — lazy-loaded when Personnel Reports opens)
    allVisits.forEach(visit => {
      const visitDate = new Date(visit.date || visit.firstVisitDate);
      if (visitDate >= filterStartDate && visitDate <= filterEndDate) {
        const stats = statsMap.get(visit.employeeId);
        if (stats) {
          stats.totalVisits++;
          stats.totalVisitDays += (visit.period || 1);
          if (visit.status) {
            stats.completedVisits++;
          } else {
            stats.inProgressVisits++;
          }
          if (visit.projectId) {
            stats.projectsWorkedOn.add(visit.projectId);
          }
          // Track visit types
          const visitType = visit.visitType || 'Standart';
          stats.visitTypes[visitType] = (stats.visitTypes[visitType] || 0) + 1;
        }
      }
    });

    // Process calendar jobs (uses full dataset)
    allCalendarJobs.forEach(job => {
      const jobDate = new Date(job.startDate);
      if (jobDate >= filterStartDate && jobDate <= filterEndDate) {
        const stats = statsMap.get(job.employeeId);
        if (stats) {
          stats.totalJobs++;
          if (job.status) {
            stats.completedJobs++;
          }
        }
      }
    });

    // Convert to array and calculate averages
    const statsArray = Array.from(statsMap.values())
      .map(stat => ({
        ...stat,
        projectsWorkedOn: stat.projectsWorkedOn.size,
        completionRate: stat.totalVisits > 0 ? Math.round((stat.completedVisits / stat.totalVisits) * 100) : 0,
        averageVisitDuration: stat.totalVisits > 0 ? Math.round(stat.totalVisitDays / stat.totalVisits * 10) / 10 : 0
      }))
      .filter(stat => stat.totalVisits > 0 || stat.totalJobs > 0); // Only show employees with activity

    // Sort by selected field and direction
    const sortedArray = [...statsArray].sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'employeeName':
          aVal = a.employeeName.toLowerCase();
          bVal = b.employeeName.toLowerCase();
          break;
        case 'totalVisits':
          aVal = a.totalVisits;
          bVal = b.totalVisits;
          break;
        case 'completedVisits':
          aVal = a.completedVisits;
          bVal = b.completedVisits;
          break;
        case 'inProgressVisits':
          aVal = a.inProgressVisits;
          bVal = b.inProgressVisits;
          break;
        case 'projectsWorkedOn':
          aVal = a.projectsWorkedOn;
          bVal = b.projectsWorkedOn;
          break;
        case 'averageVisitDuration':
          aVal = a.averageVisitDuration;
          bVal = b.averageVisitDuration;
          break;
        case 'completionRate':
          aVal = a.completionRate;
          bVal = b.completionRate;
          break;
        case 'totalJobs':
          aVal = a.totalJobs;
          bVal = b.totalJobs;
          break;
        case 'completedJobs':
          aVal = a.completedJobs;
          bVal = b.completedJobs;
          break;
        default:
          aVal = a.totalVisits;
          bVal = b.totalVisits;
      }
      
      if (sortField === 'employeeName') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal, 'tr') : bVal.localeCompare(aVal, 'tr');
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });

    // Calculate totals
    const totals = {
      totalVisits: statsArray.reduce((sum, s) => sum + s.totalVisits, 0),
      completedVisits: statsArray.reduce((sum, s) => sum + s.completedVisits, 0),
      inProgressVisits: statsArray.reduce((sum, s) => sum + s.inProgressVisits, 0),
      totalJobs: statsArray.reduce((sum, s) => sum + s.totalJobs, 0),
      completedJobs: statsArray.reduce((sum, s) => sum + s.completedJobs, 0),
      totalVisitDays: statsArray.reduce((sum, s) => sum + s.totalVisitDays, 0),
      uniqueProjects: new Set(statsArray.flatMap(s => [...(allVisits.filter(v => v.employeeId === s.employeeId).map(v => v.projectId))])).size,
      activeEmployees: statsArray.length
    };

    // Determine top performers
    const topByVisits = [...statsArray].sort((a, b) => b.totalVisits - a.totalVisits).slice(0, 3);
    const topByCompletion = [...statsArray].filter(s => s.totalVisits >= 3).sort((a, b) => b.completionRate - a.completionRate).slice(0, 3);

    return {
      employees: sortedArray,
      totals,
      topByVisits,
      topByCompletion,
      filterStartDate,
      filterEndDate
    };
  }, [allVisits, allCalendarJobs, employees, reportPeriod, customReportStartDate, customReportEndDate, sortField, sortDirection]);

  // Handle sort column click
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Handle report filter change
  const handleReportFilterChange = (field, value) => {
    setReportFilters(prev => ({ ...prev, [field]: value }));
  };

  // Filter personnel statistics based on column filters
  const filteredPersonnelEmployees = useMemo(() => {
    return personnelStatistics.employees.filter(stat => {
      // Employee name filter (text search)
      if (reportFilters.employeeName && 
          !stat.employeeName.toLowerCase().includes(reportFilters.employeeName.toLowerCase())) {
        return false;
      }
      // Numeric filters: support exact match, >, <, >=, <=
      const numericFields = ['totalVisits', 'completedVisits', 'totalJobs', 'completedJobs', 'projectsWorkedOn', 'completionRate'];
      for (const field of numericFields) {
        const filterVal = reportFilters[field].trim();
        if (!filterVal) continue;
        const statVal = stat[field];
        
        if (filterVal.startsWith('>=')) {
          const num = parseFloat(filterVal.slice(2));
          if (!isNaN(num) && statVal < num) return false;
        } else if (filterVal.startsWith('<=')) {
          const num = parseFloat(filterVal.slice(2));
          if (!isNaN(num) && statVal > num) return false;
        } else if (filterVal.startsWith('>')) {
          const num = parseFloat(filterVal.slice(1));
          if (!isNaN(num) && statVal <= num) return false;
        } else if (filterVal.startsWith('<')) {
          const num = parseFloat(filterVal.slice(1));
          if (!isNaN(num) && statVal >= num) return false;
        } else {
          const num = parseFloat(filterVal);
          if (!isNaN(num) && statVal !== num) return false;
        }
      }
      return true;
    });
  }, [personnelStatistics.employees, reportFilters]);

  // Get employee visits for the modal (uses full dataset)
  const getEmployeeVisits = useCallback((employeeId) => {
    const { filterStartDate, filterEndDate } = personnelStatistics;
    return allVisits.filter(visit => {
      const visitDate = new Date(visit.date || visit.firstVisitDate);
      return visit.employeeId === employeeId &&
             visitDate >= filterStartDate &&
             visitDate <= filterEndDate;
    }).sort((a, b) => new Date(b.date || b.firstVisitDate) - new Date(a.date || a.firstVisitDate));
  }, [allVisits, personnelStatistics]);

  // Get employee jobs for the modal (uses full dataset)
  const getEmployeeJobs = useCallback((employeeId) => {
    const { filterStartDate, filterEndDate } = personnelStatistics;
    return allCalendarJobs.filter(job => {
      const jobDate = new Date(job.startDate);
      return job.employeeId === employeeId &&
             jobDate >= filterStartDate &&
             jobDate <= filterEndDate;
    }).sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
  }, [allCalendarJobs, personnelStatistics]);

  // Handle employee row click
  const handleEmployeeClick = (stat) => {
    const employeeVisits = getEmployeeVisits(stat.employeeId);
    const employeeJobs = getEmployeeJobs(stat.employeeId);
    setSelectedEmployeeStats({
      ...stat,
      visits: employeeVisits,
      jobs: employeeJobs
    });
    setShowEmployeeModal(true);
  };

  // Memoized helper functions using lookup maps
  const getEmployeeName = useCallback((empId) => {
    const employee = employeeMap.get(empId);
    return employee ? `${employee.eName} ${employee.eSurname}` : '';
  }, [employeeMap]);

  const getProjectName = useCallback((projectId) => {
    const project = projectMap.get(projectId);
    return project ? (project.titleShort || project.projectName || '') : '';
  }, [projectMap]);

  const getVisitsForDay = useCallback((date) => {
    return visits.filter((visit) => {
      // Only show visits for active employees (O(1) lookup using Map)
      const employee = employeeMap.get(visit.employeeId);
      if (!employee || employee.Active !== 1) return false;

      const visitDateStr = visit.date || visit.firstVisitDate;
      if (!visitDateStr) return false;
      const firstVisitDate = normalizeDate(new Date(visitDateStr));
      if (isNaN(firstVisitDate.getTime())) return false;

      const currentDateToCheck = normalizeDate(
        new Date(currentDate.getFullYear(), currentDate.getMonth(), date)
      );

      // On the operation management calendar, only show visits on their first day
      return (
        currentDateToCheck.getTime() === firstVisitDate.getTime() &&
        currentDateToCheck.getMonth() === currentDate.getMonth() &&
        currentDateToCheck.getFullYear() === currentDate.getFullYear()
      );
    });
  }, [visits, employeeMap, currentDate, normalizeDate]);

  const isFirstDayOfVisit = (visit, date) => {
    const visitDate = normalizeDate(new Date(visit.date || visit.firstVisitDate));
    const checkDate = normalizeDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth(), date)
    );

    return visitDate.getTime() === checkDate.getTime();
  };

  const getRemainingDays = (visit, date) => {
    const visitDate = new Date(visit.date || visit.firstVisitDate);
    const currentDateToCheck = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      date
    );
    
    const period = visit.period || 1;
    let endDate;
    
    if (period >= 0) {
      // Positive period: end date is forward from firstVisitDate
      endDate = new Date(visitDate);
      endDate.setDate(visitDate.getDate() + (period - 1));
    } else {
      // Negative period: end date is the firstVisitDate itself
      endDate = new Date(visitDate);
    }

    const remainingDays =
      Math.ceil((endDate - currentDateToCheck) / (1000 * 60 * 60 * 24)) + 1;
    return remainingDays;
  };

  const getJobsForDay = useCallback((date) => {
    return calendarJobs.filter((job) => {
      // Only show jobs for active employees (O(1) lookup using Map)
      const employee = employeeMap.get(job.employeeId);
      if (!employee || employee.Active !== 1) return false;

      const startDate = normalizeDate(new Date(job.startDate));
      const endDate = normalizeDate(new Date(job.endDate));

      const currentDateToCheck = normalizeDate(
        new Date(currentDate.getFullYear(), currentDate.getMonth(), date)
      );

      return (
        currentDateToCheck >= startDate &&
        currentDateToCheck <= endDate &&
        currentDateToCheck.getMonth() === currentDate.getMonth() &&
        currentDateToCheck.getFullYear() === currentDate.getFullYear()
      );
    });
  }, [calendarJobs, employeeMap, currentDate, normalizeDate]);

  const isFirstDayOfJob = (job, date) => {
    const jobDate = normalizeDate(new Date(job.startDate));
    const checkDate = normalizeDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth(), date)
    );

    return jobDate.getTime() === checkDate.getTime();
  };

  const getDayNumber = (startDate, currentDay) => {
    const start = normalizeDate(new Date(startDate));
    const current = normalizeDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDay)
    );

    const diffTime = Math.abs(current - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
  };

  const isVisitOverdue = (visit) => {
    if (visit.status) return false; // completed visits are not overdue
    const visitDate = new Date(visit.date || visit.firstVisitDate);
    const period = visit.period || 1;
    let endDate;
    if (period >= 0) {
      endDate = new Date(visitDate);
      endDate.setDate(visitDate.getDate() + (period - 1));
    } else {
      endDate = new Date(visitDate);
    }
    // Add 72-hour grace period after visit end date
    const deadline = new Date(endDate);
    deadline.setHours(23, 59, 59, 999);
    deadline.setTime(deadline.getTime() + (72 * 60 * 60 * 1000));
    return deadline < new Date();
  };

  /**
   * Status class for a visit pill.
   *
   * Pass `date` on views that render one pill per day of the visit window
   * (week/day): only the day the visit was actually completed stays solid
   * green, the rest of the window renders as closed. The month view shows a
   * single pill for the whole visit, so it omits `date` and keeps the solid
   * completed look, with the date surfaced as a chip instead.
   */
  const getVisitStatusClass = (visit, date) => {
    if (visit.status) {
      if (date) {
        const completionState = getCompletionState(visit, date);
        if (completionState === COMPLETION_STATE.COMPLETED_DAY) {
          return `${styles.visitCompleted} ${styles.completedOnDay}`;
        }
        if (completionState === COMPLETION_STATE.WINDOW_CLOSED) {
          return styles.windowClosed;
        }
      }
      return styles.visitCompleted;
    }
    if (isVisitOverdue(visit)) return styles.visitOverdue;
    return styles.visitInProgress;
  };

  /** Short label for status badges. */
  const getVisitStatusText = (visit) => {
    if (visit.status) return 'Tamamlandı';
    if (isVisitOverdue(visit)) return 'Tamamlanmadı';
    return 'Devam Ediyor';
  };

  /** Verbose status for tooltips, including when the visit was completed. */
  const getVisitStatusDetail = (visit, date) => {
    if (visit.status) {
      return getCompletionTooltipLine(visit, date) || 'Tamamlandı';
    }
    if (isVisitOverdue(visit)) return 'Tamamlanmadı';
    return 'Devam Ediyor';
  };

  const handleShowTooltip = (e, content) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipContent(content);

    const tooltipHeight = 100; // approximate
    const tooltipWidth = 250;
    const spaceBelow = window.innerHeight - rect.bottom;
    const showAbove = spaceBelow < tooltipHeight + 10;

    let top = showAbove ? rect.top - tooltipHeight - 8 : rect.bottom + 8;
    let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);

    // Keep tooltip within viewport horizontally
    if (left < 8) left = 8;
    if (left + tooltipWidth > window.innerWidth - 8) left = window.innerWidth - tooltipWidth - 8;

    setTooltipPosition({ top, left });
    setShowTooltip(true);
  };

  const handleHideTooltip = () => {
    setShowTooltip(false);
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = firstDayOfMonth(currentDate);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className={styles.calendarCellEmpty}></div>);
    }

    // Add days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayVisits = getVisitsForDay(day);
      const dayJobs = getJobsForDay(day);
      
      const isToday =
        day === new Date().getDate() &&
        currentDate.getMonth() === new Date().getMonth() &&
        currentDate.getFullYear() === new Date().getFullYear();

      // O güne denk gelen onaylanmış izinler
      const dayLeaves = getLeavesForDate(
        leaveDayMap,
        new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
      );
      const isOnLeave = dayLeaves.length > 0;
        
      // Vizit ve iş kartları; saat sıralı tek listede türe göre çağrılır
      const renderVisitChip = (visit) => {

              const projectName = getProjectName(visit.projectId);
              const employeeName = getEmployeeName(visit.employeeId);
              // The month view shows one pill per visit rather than one per day,
              // so the completion date is surfaced as a chip on the pill itself.
              const completedOn = formatCompletionDateShort(visit);
              // Overlapping visits of the same patient look identical once the
              // chip truncates, so the visit number leads and never shrinks.
              const patientLabel = normalizePatientId(visit.patientId);
              const visitNumberLabel = visit.visitNumber ? `V${visit.visitNumber}` : '';
              const pendingCompletionLabels = getPendingCompletionLabels(visit);
              return (
                <div
                  key={visit.id}
                  onClick={() => handleVisitClick(visit)}
                  className={`${styles.visitItem} ${
                    isFirstDayOfVisit(visit, day) ? styles.visitFirstDay : ''
                  } ${getVisitStatusClass(visit)}`}
                  onMouseEnter={(e) => {
                    const tooltipContent = [
                      `Proje: ${projectName || 'Belirtilmemiş'}`,
                      `Çalışan: ${employeeName || 'Belirtilmemiş'}`,
                      `Hasta ID: ${patientLabel || 'Belirtilmemiş'}`,
                      visit.visitNumber ? `Visit: ${visit.visitNumber}. Visit` : null,
                      `Gün: ${getDayNumber(visit.date || visit.firstVisitDate, day)}`,
                      `Durum: ${getVisitStatusDetail(visit)}`,
                    ].filter(Boolean).join('\n');
                    handleShowTooltip(e, tooltipContent);
                  }}
                  onMouseLeave={handleHideTooltip}
                >
                  {visitNumberLabel && (
                    <span className={styles.visitNumberBadge}>{visitNumberLabel}</span>
                  )}
                  <span className={styles.visitItemLabel}>
                    {projectName && employeeName ? `${projectName} - ${employeeName}` : (projectName || employeeName || patientLabel)} ({getDayNumber(visit.date || visit.firstVisitDate, day)}. Gün)
                  </span>
                  {completedOn && (
                    <span className={styles.completedOnChip}>
                      <FaCheck aria-hidden="true" /> {completedOn}
                    </span>
                  )}
                  {/* Completed, but D — Tamamlanma Tarihleri are outstanding */}
                  {pendingCompletionLabels.length > 0 && (
                    <span
                      className={styles.signaturePendingBadge}
                      title={`Eksik tamamlanma tarihi: ${pendingCompletionLabels.join(', ')}`}
                    >
                      Eksik
                    </span>
                  )}
                </div>
              );
      };

      const renderJobChip = (job) => (

              <div
                key={job.id}
                className={`${styles.visitItem} ${
                  isFirstDayOfJob(job, day) ? styles.visitFirstDay : ''
                } ${styles.calendarJob} ${job.status ? styles.jobCompleted : ''}`}
                onClick={() => handleJobClick(job)}
                style={isRestrictedUser ? { cursor: 'pointer' } : {}}
                onMouseEnter={(e) => {
                  const employeeName = getEmployeeName(job.employeeId);
                  const tooltipContent = `
                    ${job.title}
                    Çalışan: ${employeeName || 'Belirtilmemiş'}
                    Gün: ${getDayNumber(job.startDate, day)}
                    Durum: ${job.status ? 'Tamamlandı' : 'Devam Ediyor'}
                  `;
                  handleShowTooltip(e, tooltipContent);
                }}
                onMouseLeave={handleHideTooltip}
              >
                <span className={styles.visitItemLabel}>
                  {job.title} ({getDayNumber(job.startDate, day)}. Gün)
                </span>
              </div>
      );

      days.push(
        <div
          key={day}
          className={`${styles.calendarCell} ${isToday ? styles.calendarCellHighlighted : ''} ${isOnLeave ? styles.calendarCellLeave : ''}`}
        >
          <div className={styles.dayNumber}>{day}</div>
          {isOnLeave && (
            <div
              className={styles.leaveBadge}
              onMouseEnter={(e) =>
                handleShowTooltip(
                  e,
                  dayLeaves
                    .map((leave) => `İzinli: ${leave.label} (${leave.leaveDays || '?'} gün)`)
                    .join('\n')
                )
              }
              onMouseLeave={handleHideTooltip}
            >
              <FaUmbrellaBeach className={styles.leaveBadgeIcon} aria-hidden="true" />
              <span className={styles.leaveBadgeText}>{describeLeaves(dayLeaves)}</span>
            </div>
          )}
          <div className={styles.visitsList}>
            {/* Vizitler ve işler tek listede, saat sırasına göre gösterilir */}
            {mergeByTime(dayVisits, dayJobs).map((entry) =>
              entry.kind === 'visit'
                ? renderVisitChip(entry.item)
                : renderJobChip(entry.item)
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  // Existing calendar helper functions
  const getMonthName = (date) => {
    const monthNames = [
      'Ocak',
      'Şubat',
      'Mart',
      'Nisan',
      'Mayıs',
      'Haziran',
      'Temmuz',
      'Ağustos',
      'Eylül',
      'Ekim',
      'Kasım',
      'Aralık'
    ];
    return monthNames[date.getMonth()];
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const firstDayOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    let dayOfWeek = new Date(year, month, 1).getDay();
    // Convert Sunday (0) to 7, and subtract 1 from all other days
    // This makes Monday (1) become 0, Tuesday (2) become 1, etc.
    return dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  };

  // Get the week dates for the current date
  const getWeekDates = (date) => {
    const startOfWeek = new Date(date);
    const dayOfWeek = startOfWeek.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Adjust for Monday start
    startOfWeek.setDate(startOfWeek.getDate() + diff);
    
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      weekDates.push(day);
    }
    return weekDates;
  };

  // Get visits for a specific date (for week/day views)
  const getVisitsForDate = useCallback((date) => {
    return visits.filter((visit) => {
      const employee = employeeMap.get(visit.employeeId);
      if (!employee || employee.Active !== 1) return false;

      const visitDateStr = visit.date || visit.firstVisitDate;
      if (!visitDateStr) return false;
      const firstVisitDate = normalizeDate(new Date(visitDateStr));
      if (isNaN(firstVisitDate.getTime())) return false;
      const period = visit.period || 1;

      let startDate, endDate;
      if (period >= 0) {
        // Positive period: start from firstVisitDate, extend forward
        startDate = new Date(firstVisitDate);
        endDate = new Date(firstVisitDate);
        endDate.setDate(firstVisitDate.getDate() + (period - 1));
      } else {
        // Negative period: start from (firstVisitDate + period), end at firstVisitDate
        startDate = new Date(firstVisitDate);
        startDate.setDate(firstVisitDate.getDate() + period + 1);
        endDate = new Date(firstVisitDate);
      }

      const checkDate = normalizeDate(date);
      return checkDate >= startDate && checkDate <= endDate;
    });
  }, [visits, employeeMap, normalizeDate]);

  // Get jobs for a specific date (for week/day views)
  const getJobsForDate = useCallback((date) => {
    return calendarJobs.filter((job) => {
      const employee = employeeMap.get(job.employeeId);
      if (!employee || employee.Active !== 1) return false;

      const startDate = normalizeDate(new Date(job.startDate));
      const endDate = normalizeDate(new Date(job.endDate));
      const checkDate = normalizeDate(date);

      return checkDate >= startDate && checkDate <= endDate;
    });
  }, [calendarJobs, employeeMap, normalizeDate]);

  // Navigation for week view
  const handlePrevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  // Navigation for day view
  const handlePrevDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  // Get day number for a specific date
  const getDayNumberForDate = (startDate, date) => {
    const start = normalizeDate(new Date(startDate));
    const current = normalizeDate(date);
    const diffTime = Math.abs(current - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
  };

  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('tr-TR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Format short date
  const formatShortDate = (date) => {
    return date.toLocaleDateString('tr-TR', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short'
    });
  };

  // Render week view
  const renderWeekView = () => {
    const weekDates = getWeekDates(currentDate);
    const weekDays = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
    
    return (
      <div className={styles.weekViewContainer}>
        <div className={styles.weekHeader}>
          {weekDates.map((date, index) => {
            const isToday = normalizeDate(date).getTime() === normalizeDate(new Date()).getTime();
            return (
              <div key={index} className={`${styles.weekDayHeader} ${isToday ? styles.weekDayToday : ''}`}>
                <span className={styles.weekDayName}>{weekDays[index]}</span>
                <span className={styles.weekDayDate}>{date.getDate()}</span>
              </div>
            );
          })}
        </div>
        <div className={styles.weekGrid}>
          {weekDates.map((date, index) => {
            const dayVisits = getVisitsForDate(date);
            const dayJobs = getJobsForDate(date);
            const isToday = normalizeDate(date).getTime() === normalizeDate(new Date()).getTime();
            const dayLeaves = getLeavesForDate(leaveDayMap, date);
            const isOnLeave = dayLeaves.length > 0;

            return (
              <div
                key={index}
                className={`${styles.weekDayColumn} ${isToday ? styles.weekColumnToday : ''} ${isOnLeave ? styles.weekColumnLeave : ''}`}
              >
                {isOnLeave && (
                  <div
                    className={styles.leaveBadge}
                    title={dayLeaves
                      .map((leave) => `${leave.label} (${leave.leaveDays || '?'} gün)`)
                      .join('\n')}
                  >
                    <FaUmbrellaBeach className={styles.leaveBadgeIcon} aria-hidden="true" />
                    <span className={styles.leaveBadgeText}>{describeLeaves(dayLeaves)}</span>
                  </div>
                )}
                {/* Vizitler ve işler tek listede, saat sırasına göre */}
                {mergeByTime(dayVisits, dayJobs).map((entry) => {
                  if (entry.kind === 'visit') {
                    const visit = entry.item;

                  const projectName = getProjectName(visit.projectId);
                  const employeeName = getEmployeeName(visit.employeeId);
                  const completionState = getCompletionState(visit, date);
                  return (
                    <div
                      key={visit.id}
                      className={`${styles.weekVisitItem} ${getVisitStatusClass(visit, date)}`}
                      onClick={() => handleVisitClick(visit)}
                      title={getVisitStatusDetail(visit, date)}
                    >
                      <div className={styles.weekItemTitle}>
                        {projectName || 'Proje Belirtilmemiş'}
                      </div>
                      <div className={styles.weekItemSubtitle}>
                        <FaUser className={styles.weekItemIcon} /> {employeeName || 'Çalışan Belirtilmemiş'}
                      </div>
                      <div className={styles.weekItemBadge}>
                        {getDayNumberForDate(visit.date || visit.firstVisitDate, date)}. Gün
                      </div>
                      {completionState === COMPLETION_STATE.COMPLETED_DAY && (
                        <div className={`${styles.weekItemStatusBadge} ${styles.weekItemStatusCompleted}`}>
                          <FaCheck aria-hidden="true" /> {formatCompletionDateShort(visit)} tamamlandı
                        </div>
                      )}
                      {completionState === COMPLETION_STATE.WINDOW_CLOSED && (
                        <div className={`${styles.weekItemStatusBadge} ${styles.weekItemStatusClosed}`}>
                          {formatCompletionDateShort(visit)} tamamlandı
                        </div>
                      )}
                    </div>
                  );
                  }
                  const job = entry.item;

                  const employeeName = getEmployeeName(job.employeeId);
                  return (
                    <div
                      key={job.id}
                      className={`${styles.weekVisitItem} ${styles.calendarJob} ${job.status ? styles.jobCompleted : ''}`}
                      onClick={() => handleJobClick(job)}
                    >
                      <div className={styles.weekItemTitle}>{job.title}</div>
                      <div className={styles.weekItemSubtitle}>
                        <FaUser className={styles.weekItemIcon} /> {employeeName || 'Çalışan Belirtilmemiş'}
                      </div>
                      <div className={styles.weekItemBadge}>
                        {getDayNumberForDate(job.startDate, date)}. Gün
                      </div>
                      <div className={`${styles.weekItemStatusBadge} ${job.status ? styles.weekItemStatusCompleted : styles.weekItemStatusPending}`}>
                        {job.status ? 'Tamamlandı' : 'Devam Ediyor'}
                      </div>
                    </div>
                  );
                })}
                {dayVisits.length === 0 && dayJobs.length === 0 && !isOnLeave && (
                  <div className={styles.weekEmptyDay}>
                    <span>Etkinlik yok</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render day view
  const renderDayView = () => {
    const dayVisits = getVisitsForDate(currentDate);
    const dayJobs = getJobsForDate(currentDate);
    const isToday = normalizeDate(currentDate).getTime() === normalizeDate(new Date()).getTime();
    const dayLeaves = getLeavesForDate(leaveDayMap, currentDate);
    
    return (
      <div className={styles.dayViewContainer}>
        <div className={`${styles.dayViewHeader} ${isToday ? styles.dayViewHeaderToday : ''}`}>
          <div className={styles.dayViewDate}>
            <span className={styles.dayViewWeekday}>
              {currentDate.toLocaleDateString('tr-TR', { weekday: 'long' })}
            </span>
            <span className={styles.dayViewFullDate}>
              {currentDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </div>
          {isToday && <span className={styles.todayBadge}>Bugün</span>}
        </div>
        
        <div className={styles.dayViewContent}>
          {dayLeaves.length > 0 && (
            <div className={styles.leaveNotice}>
              <FaUmbrellaBeach className={styles.leaveNoticeIcon} aria-hidden="true" />
              <span>
                Bu gün izinli: {describeLeaves(dayLeaves)}
              </span>
            </div>
          )}
          {dayVisits.length === 0 && dayJobs.length === 0 ? (
            <div className={styles.dayEmptyState}>
              <FaCalendarAlt className={styles.emptyIcon} />
              <span className={styles.emptyText}>Bu gün için planlanmış etkinlik bulunmuyor</span>
            </div>
          ) : (
            <div className={styles.dayEventsList}>
              {/* Vizitler ve işler tek listede, saat sırasına göre */}
              {mergeByTime(dayVisits, dayJobs).map((entry) => {
                if (entry.kind === 'visit') {
                  const visit = entry.item;

                const projectName = getProjectName(visit.projectId);
                const employeeName = getEmployeeName(visit.employeeId);
                const completionState = getCompletionState(visit, currentDate);
                const isCompletionDay = completionState === COMPLETION_STATE.COMPLETED_DAY;
                const isClosedWindowDay = completionState === COMPLETION_STATE.WINDOW_CLOSED;
                return (
                  <div
                    key={visit.id}
                    className={`${styles.dayEventCard} ${visit.status ? styles.dayEventCompleted : isVisitOverdue(visit) ? styles.dayEventOverdue : styles.dayEventInProgress} ${isClosedWindowDay ? styles.dayEventWindowClosed : ''}`}
                    onClick={() => handleVisitClick(visit)}
                  >
                    <div className={styles.dayEventHeader}>
                      <div className={styles.dayEventType}>
                        <FaCalendarCheck className={styles.dayEventTypeIcon} />
                        <span>Ziyaret</span>
                      </div>
                      <span className={`${styles.dayEventStatus} ${visit.status ? styles.statusCompleted : isVisitOverdue(visit) ? styles.statusOverdue : styles.statusInProgress} ${isClosedWindowDay ? styles.statusWindowClosed : ''}`}>
                        {isCompletionDay && <FaCheck aria-hidden="true" />}
                        {isClosedWindowDay ? 'Pencere kapandı' : getVisitStatusText(visit)}
                      </span>
                    </div>
                    <div className={styles.dayEventTitle}>{projectName || 'Proje Belirtilmemiş'}</div>
                    <div className={styles.dayEventDetails}>
                      <div className={styles.dayEventDetail}>
                        <FaUser className={styles.dayEventDetailIcon} />
                        <span>{employeeName || 'Çalışan Belirtilmemiş'}</span>
                      </div>
                      <div className={styles.dayEventDetail}>
                        <FaInfoCircle className={styles.dayEventDetailIcon} />
                        <span>{getDayNumberForDate(visit.date || visit.firstVisitDate, currentDate)}. Gün / {visit.period || 1} Günlük Ziyaret</span>
                      </div>
                      {visit.status && (
                        <div className={`${styles.dayEventDetail} ${styles.dayEventCompletionDetail}`}>
                          <FaCheck className={styles.dayEventDetailIcon} />
                          <span>
                            {formatCompletionDate(visit, { withTime: true })
                              ? `Tamamlanma: ${formatCompletionDate(visit, { withTime: true })}`
                              : 'Tamamlanma tarihi kayıtlı değil'}
                          </span>
                        </div>
                      )}
                    </div>
                    {visit.info && (
                      <div className={styles.dayEventInfo}>
                        <span>{visit.info}</span>
                      </div>
                    )}
                  </div>
                );
                }
                const job = entry.item;

                const employeeName = getEmployeeName(job.employeeId);
                return (
                  <div
                    key={job.id}
                    className={`${styles.dayEventCard} ${job.status ? styles.dayEventCompleted : styles.dayEventPlanned}`}
                    onClick={() => handleJobClick(job)}
                  >
                    <div className={styles.dayEventHeader}>
                      <div className={styles.dayEventType}>
                        <FaCalendarAlt className={styles.dayEventTypeIcon} />
                        <span>İş</span>
                      </div>
                      <span className={`${styles.dayEventStatus} ${job.status ? styles.statusCompleted : styles.statusPlanned}`}>
                        {job.status ? 'Tamamlandı' : 'Devam Ediyor'}
                      </span>
                    </div>
                    <div className={styles.dayEventTitle}>{job.title}</div>
                    <div className={styles.dayEventDetails}>
                      <div className={styles.dayEventDetail}>
                        <FaUser className={styles.dayEventDetailIcon} />
                        <span>{employeeName || 'Çalışan Belirtilmemiş'}</span>
                      </div>
                      <div className={styles.dayEventDetail}>
                        <FaInfoCircle className={styles.dayEventDetailIcon} />
                        <span>{getDayNumberForDate(job.startDate, currentDate)}. Gün</span>
                      </div>
                    </div>
                    {job.description && (
                      <div className={styles.dayEventInfo}>
                        <span>{job.description}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render list view
  const renderListView = () => {
    // Get all visits and jobs for current month, sorted by date
    const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    
    // Combine visits and jobs into a single list with date info
    const allEvents = [];
    
    visits.forEach(visit => {
      const employee = employeeMap.get(visit.employeeId);
      if (!employee || employee.Active !== 1) return;
      
      const visitDate = new Date(visit.date || visit.firstVisitDate);
      if (visitDate >= monthStart && visitDate <= monthEnd) {
        allEvents.push({
          type: 'visit',
          date: visitDate,
          data: visit,
          projectName: getProjectName(visit.projectId),
          employeeName: getEmployeeName(visit.employeeId)
        });
      }
    });
    
    calendarJobs.forEach(job => {
      const employee = employeeMap.get(job.employeeId);
      if (!employee || employee.Active !== 1) return;
      
      const jobDate = new Date(job.startDate);
      if (jobDate >= monthStart && jobDate <= monthEnd) {
        allEvents.push({
          type: 'job',
          date: jobDate,
          data: job,
          employeeName: getEmployeeName(job.employeeId)
        });
      }
    });
    
    // Sort by date
    // Önce tarihe, aynı gün içinde saate göre sırala; saati girilmemişler sona
    allEvents.sort((a, b) => {
      const byDate = a.date - b.date;
      if (byDate !== 0) return byDate;
      const aMinutes = timeToMinutes(
        a.type === 'visit' ? getVisitTime(a.data) : getJobTime(a.data)
      );
      const bMinutes = timeToMinutes(
        b.type === 'visit' ? getVisitTime(b.data) : getJobTime(b.data)
      );
      if (aMinutes === null && bMinutes === null) return 0;
      if (aMinutes === null) return 1;
      if (bMinutes === null) return -1;
      return aMinutes - bMinutes;
    });
    
    // Group by date
    const groupedEvents = {};
    allEvents.forEach(event => {
      const dateKey = event.date.toISOString().split('T')[0];
      if (!groupedEvents[dateKey]) {
        groupedEvents[dateKey] = [];
      }
      groupedEvents[dateKey].push(event);
    });
    
    return (
      <div className={styles.listViewContainer}>
        {Object.keys(groupedEvents).length === 0 ? (
          <div className={styles.listEmptyState}>
            <FaCalendarAlt className={styles.emptyIcon} />
            <span className={styles.emptyText}>Bu ay için planlanmış etkinlik bulunmuyor</span>
          </div>
        ) : (
          Object.keys(groupedEvents).map(dateKey => {
            const date = new Date(dateKey);
            const isToday = normalizeDate(date).getTime() === normalizeDate(new Date()).getTime();
            
            return (
              <div key={dateKey} className={styles.listDateGroup}>
                <div className={`${styles.listDateHeader} ${isToday ? styles.listDateToday : ''}`}>
                  <div className={styles.listDateInfo}>
                    <span className={styles.listDateDay}>{date.getDate()}</span>
                    <div className={styles.listDateText}>
                      <span className={styles.listDateWeekday}>
                        {date.toLocaleDateString('tr-TR', { weekday: 'long' })}
                      </span>
                      <span className={styles.listDateMonth}>
                        {date.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                  {isToday && <span className={styles.listTodayBadge}>Bugün</span>}
                  <span className={styles.listEventCount}>{groupedEvents[dateKey].length} etkinlik</span>
                </div>
                <div className={styles.listEventsContainer}>
                  {groupedEvents[dateKey].map((event, index) => (
                    <div
                      key={`${event.type}-${event.data.id}-${index}`}
                      className={`${styles.listEventItem} ${
                        event.data.status ? styles.listEventCompleted : 
                        (event.type === 'visit' ? styles.listEventInProgress : styles.listEventPlanned)
                      }`}
                      onClick={() => event.type === 'visit' ? handleVisitClick(event.data) : handleJobClick(event.data)}
                    >
                      <div className={styles.listEventIndicator}></div>
                      <div className={styles.listEventContent}>
                        <div className={styles.listEventTop}>
                          <span className={styles.listEventTitle}>
                            {event.type === 'visit' ? (event.projectName || 'Proje Belirtilmemiş') : event.data.title}
                          </span>
                          <span className={`${styles.listEventTypeBadge} ${
                            event.type === 'visit' ? styles.listEventTypeVisit : styles.listEventTypeJob
                          }`}>
                            {event.type === 'visit' ? 'Ziyaret' : 'İş'}
                          </span>
                        </div>
                        <div className={styles.listEventMeta}>
                          <span className={styles.listEventEmployee}>
                            <FaUser className={styles.listEventMetaIcon} />
                            {event.employeeName || 'Çalışan Belirtilmemiş'}
                          </span>
                          {event.type === 'visit' && (
                            <span className={styles.listEventPeriod}>
                              <FaCalendarCheck className={styles.listEventMetaIcon} />
                              {event.data.period || 1} Günlük
                            </span>
                          )}
                          <span className={`${styles.listEventStatusBadge} ${
                            event.data.status ? styles.statusBadgeCompleted :
                            (event.type === 'visit' ? styles.statusBadgeInProgress : styles.statusBadgePlanned)
                          }`}>
                            {event.data.status && <FaCheck aria-hidden="true" />}
                            {event.data.status ? 'Tamamlandı' : 'Devam Ediyor'}
                            {event.type === 'visit' && formatCompletionDateShort(event.data)
                              ? ` · ${formatCompletionDateShort(event.data)}`
                              : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  // Render content based on view mode
  const renderContent = () => {
    switch (viewMode) {
      case 'week':
        return renderWeekView();
      case 'day':
        return renderDayView();
      case 'list':
        return renderListView();
      case 'month':
      default:
        return (
          <>
            <div className={styles.daysOfWeek}>
              {['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'].map((day) => (
                <div key={day} className={styles.dayLabel}>
                  {day}
                </div>
              ))}
            </div>
            <div className={styles.calendarGrid}>
              {renderCalendarDays()}
            </div>
          </>
        );
    }
  };

  // Get navigation title based on view mode
  const getNavigationTitle = () => {
    switch (viewMode) {
      case 'week':
        const weekDates = getWeekDates(currentDate);
        return `${formatShortDate(weekDates[0])} - ${formatShortDate(weekDates[6])}`;
      case 'day':
        return formatDate(currentDate);
      case 'list':
      case 'month':
      default:
        return `${getMonthName(currentDate)} ${currentDate.getFullYear()}`;
    }
  };

  // Handle navigation based on view mode
  const handlePrev = () => {
    switch (viewMode) {
      case 'week':
        handlePrevWeek();
        break;
      case 'day':
        handlePrevDay();
        break;
      default:
        handlePrevMonth();
    }
  };

  const handleNext = () => {
    switch (viewMode) {
      case 'week':
        handleNextWeek();
        break;
      case 'day':
        handleNextDay();
        break;
      default:
        handleNextMonth();
    }
  };

  const handlePrevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1)
    );
  };

  const handleTodayClick = () => {
    setCurrentDate(new Date());
  };

  const handleVisitClick = (visit) => {
    setSelectedVisit(visit);
    setShowVisitModal(true);
  };

  const handleVisitUpdate = async (updatedVisit) => {
    // Check if the visit was deleted
    if (updatedVisit.deleted) {
      // Remove the deleted visit from local state
      setVisits((prevVisits) =>
        prevVisits.filter((visit) => visit.id !== updatedVisit.id)
      );
    } else {
      // Update local state immediately for better UX
      setVisits((prevVisits) =>
        prevVisits.map((visit) => (visit.id === updatedVisit.id ? updatedVisit : visit))
      );
    }
    // Refresh data from Firestore
    await fetchDataForMonth(currentDate, selectedEmployeeId);
    // Mark the full-employee cache stale so Personnel Reports refetches next open
    setAllDataLoaded(false);
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setShowJobDeleteModal(true);
  };

  const handleToggleJobStatus = async () => {
    if (!selectedJob?.id) return;

    try {
      const jobRef = doc(db, 'CalendarJobs', selectedJob.id);
      const newStatus = !selectedJob.status;
      await updateDoc(jobRef, {
        status: newStatus,
        lastUpdated: new Date().toISOString()
      });

      // Update local state
      const updatedJob = { ...selectedJob, status: newStatus };
      setCalendarJobs(prevJobs =>
        prevJobs.map(job => job.id === selectedJob.id ? { ...job, status: newStatus } : job)
      );
      setSelectedJob(updatedJob);
      setAllDataLoaded(false);

      toast.success(newStatus ? 'İş tamamlandı olarak işaretlendi!' : 'İş devam ediyor olarak işaretlendi!');
    } catch (error) {
      console.error('Error updating job status:', error);
      toast.error('İş durumu güncellenirken bir hata oluştu');
    }
  };

  const handleDeleteJob = async () => {
    if (!selectedJob?.id) return;

    setIsDeletingJob(true);
    try {
      const jobRef = doc(db, 'CalendarJobs', selectedJob.id);
      await deleteDoc(jobRef);

      // Remove the deleted job from local state
      setCalendarJobs((prevJobs) =>
        prevJobs.filter((job) => job.id !== selectedJob.id)
      );

      toast.success('İş başarıyla silindi');
      setShowJobDeleteModal(false);
      setSelectedJob(null);

      // Refresh data from Firestore
      await fetchDataForMonth(currentDate, selectedEmployeeId);
      setAllDataLoaded(false);
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('İş silinirken bir hata oluştu');
    } finally {
      setIsDeletingJob(false);
    }
  };

  /**
   * Cascade delete — "delete this item and everything that follows it".
   *
   * Series definition (admin decision):
   *   visits → same employeeId AND same patientId (Hasta ID)
   *   jobs   → same employeeId AND same title (jobs carry no patientId)
   *
   * The anchor item is always included. The set is read straight from
   * Firestore rather than from local state, so it stays complete even when the
   * calendar is showing a single month or a different employee filter.
   */
  const getVisitDate = useCallback((visit) => {
    const raw = visit.date || visit.firstVisitDate;
    if (!raw) return null;
    const parsed = new Date(raw);
    return isNaN(parsed.getTime()) ? null : normalizeDate(parsed);
  }, [normalizeDate]);

  const buildCascadePreview = useCallback(async (item, type) => {
    if (!isAdmin) return;
    if (!item?.employeeId) {
      toast.error('Bu kayıtta çalışan bilgisi yok, seri silme yapılamaz');
      return;
    }

    // A visit with no Hasta ID must never form a series: the old '-' sentinel
    // would otherwise group every unassigned visit of the employee together.
    const seriesKey = type === 'visit' ? normalizePatientId(item.patientId) : item.title;
    if (!seriesKey) {
      toast.error(
        type === 'visit'
          ? 'Bu ziyarette Hasta ID yok, seri silme yapılamaz'
          : 'Bu işte başlık yok, seri silme yapılamaz'
      );
      return;
    }

    const anchorDate = type === 'visit'
      ? getVisitDate(item)
      : (item.startDate ? normalizeDate(new Date(item.startDate)) : null);
    if (!anchorDate || isNaN(anchorDate.getTime())) {
      toast.error('Bu kaydın tarihi okunamadı, seri silme yapılamaz');
      return;
    }

    setIsBuildingCascade(true);
    setCascade({ type, anchor: item, seriesKey, anchorDate, items: [] });

    try {
      const collectionName = type === 'visit' ? 'Visits' : 'CalendarJobs';
      const snapshot = await getDocs(query(
        collection(db, collectionName),
        where('employeeId', '==', item.employeeId)
      ));

      const matches = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((record) => {
          const key = type === 'visit' ? record.patientId : record.title;
          if (key !== seriesKey) return false;
          const recordDate = type === 'visit'
            ? getVisitDate(record)
            : (record.startDate ? normalizeDate(new Date(record.startDate)) : null);
          if (!recordDate || isNaN(recordDate.getTime())) return false;
          // Include the anchor itself and everything dated on/after it.
          return recordDate.getTime() >= anchorDate.getTime() || record.id === item.id;
        })
        .sort((a, b) => {
          const aDate = type === 'visit' ? getVisitDate(a) : normalizeDate(new Date(a.startDate));
          const bDate = type === 'visit' ? getVisitDate(b) : normalizeDate(new Date(b.startDate));
          return aDate - bDate;
        });

      setCascade({ type, anchor: item, seriesKey, anchorDate, items: matches });
    } catch (error) {
      console.error('Error building cascade preview:', error);
      toast.error('Silinecek kayıtlar listelenirken hata oluştu');
      setCascade(null);
    } finally {
      setIsBuildingCascade(false);
    }
  }, [isAdmin, getVisitDate, normalizeDate]);

  const handleConfirmCascadeDelete = async () => {
    if (!isAdmin || !cascade?.items?.length) return;

    setIsCascadeDeleting(true);
    try {
      const collectionName = cascade.type === 'visit' ? 'Visits' : 'CalendarJobs';
      // Firestore caps a batch at 500 writes.
      for (let i = 0; i < cascade.items.length; i += 450) {
        const batch = writeBatch(db);
        cascade.items.slice(i, i + 450).forEach((record) => {
          batch.delete(doc(db, collectionName, record.id));
        });
        await batch.commit();
      }

      // Hibrit proje: silinen vizitlerden türeyen tamamlanmamış eş vizitler de silinir
      if (cascade.type === 'visit') {
        try {
          await Promise.all(
            cascade.items
              .filter((record) => !isMirrorVisit(record))
              .map((record) => deleteVisitMirrors(record.id))
          );
        } catch (mirrorError) {
          console.error('Eş vizitler silinemedi:', mirrorError);
          toast.error('Bazı eş vizitler silinemedi; unblinded çalışanın takvimini kontrol edin.');
        }
      }

      const deletedIds = new Set(cascade.items.map((record) => record.id));
      if (cascade.type === 'visit') {
        setVisits((prev) => prev.filter((v) => !deletedIds.has(v.id)));
      } else {
        setCalendarJobs((prev) => prev.filter((j) => !deletedIds.has(j.id)));
      }

      toast.success(`${cascade.items.length} kayıt silindi`);
      setCascade(null);
      setShowJobDeleteModal(false);
      setSelectedJob(null);
      setSelectedVisit(null);

      await fetchDataForMonth(currentDate, selectedEmployeeId);
      setAllDataLoaded(false);
    } catch (error) {
      console.error('Error during cascade delete:', error);
      toast.error('Kayıtlar silinirken bir hata oluştu');
    } finally {
      setIsCascadeDeleting(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      toast.error('Lütfen başlangıç ve bitiş tarihlerini seçin');
      return;
    }

    try {
      // Ensure the full dataset is loaded (report spans all employees).
      let sourceVisits = allVisits;
      let sourceJobs = allCalendarJobs;
      if (!allDataLoaded) {
        const loaded = await fetchAllVisitsAndJobs();
        sourceVisits = loaded.visits;
        sourceJobs = loaded.jobs;
      }

      // Tarih aralığına göre visitleri filtrele
      const filteredVisits = sourceVisits.filter(visit => {
        const visitDate = new Date(visit.date || visit.firstVisitDate);
        const start = new Date(startDate);
        const end = new Date(endDate);
        return visitDate >= start && visitDate <= end;
      });

      // Tarih aralığına göre işleri filtrele
      const filteredJobs = sourceJobs.filter(job => {
        // İşler için startDate kullan (date field'ı yok)
        const jobStartDate = new Date(job.startDate);
        const start = new Date(startDate);
        const end = new Date(endDate);
        return jobStartDate >= start && jobStartDate <= end;
      });

      // Excel workbook oluştur
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      
      // Visits sayfası
      const visitsSheet = workbook.addWorksheet('Visitler');
      visitsSheet.columns = [
        { header: 'Tarih', key: 'date', width: 15 },
        { header: 'Çalışan', key: 'employeeName', width: 20 },
        { header: 'Müşteri', key: 'customerName', width: 25 },
        { header: 'Proje', key: 'projectName', width: 25 },
        { header: 'Açıklama', key: 'description', width: 30 },
        { header: 'Durum', key: 'status', width: 15 },
        { header: 'Başlangıç Saati', key: 'startTime', width: 15 },
        { header: 'Bitiş Saati', key: 'endTime', width: 15 },
        { header: 'Süre (Saat)', key: 'duration', width: 15 }
      ];

      // Visit verilerini ekle
      filteredVisits.forEach(visit => {
        // Çalışan bilgisini bul
        const employee = employees.find(emp => emp.id === visit.employeeId);
        const employeeName = employee ? `${employee.eName} ${employee.eSurname}` : visit.employeeId;

        const vStartTime = visit.visitStartTime || visit.startTime || '';
        const vEndTime = visit.visitEndTime || visit.endTime || '';

        // Süreyi hesapla (saat cinsinden)
        let duration = '';
        if (vStartTime && vEndTime) {
          const [sh, sm] = vStartTime.split(':').map(Number);
          const [eh, em] = vEndTime.split(':').map(Number);
          if (!isNaN(sh) && !isNaN(sm) && !isNaN(eh) && !isNaN(em)) {
            const diffMinutes = (eh * 60 + em) - (sh * 60 + sm);
            if (diffMinutes > 0) {
              duration = (diffMinutes / 60).toFixed(1);
            }
          }
        }

        visitsSheet.addRow({
          date: new Date(visit.date || visit.firstVisitDate).toLocaleDateString('tr-TR'),
          employeeName: employeeName,
          customerName: visit.patientId || '-',
          projectName: getProjectName(visit.projectId) || visit.projectId || '-',
          description: visit.info || '',
          status: visit.status ? 'Tamamlandı' : 'Devam Ediyor',
          startTime: vStartTime,
          endTime: vEndTime,
          duration: duration
        });
      });

      // İşler sayfası (sadece iş varsa oluştur)
      if (filteredJobs.length > 0) {
        const jobsSheet = workbook.addWorksheet('İşler');
        jobsSheet.columns = [
          { header: 'Tarih', key: 'date', width: 15 },
          { header: 'Çalışan', key: 'employeeName', width: 20 },
          { header: 'Müşteri', key: 'customerName', width: 25 },
          { header: 'Proje', key: 'projectName', width: 25 },
          { header: 'İş Türü', key: 'jobType', width: 20 },
          { header: 'Açıklama', key: 'description', width: 30 },
          { header: 'Durum', key: 'status', width: 15 },
          { header: 'Süre (Saat)', key: 'duration', width: 15 }
        ];

        // İş verilerini ekle
        filteredJobs.forEach(job => {
          // Çalışan bilgisini bul
          const employee = employees.find(emp => emp.id === job.employeeId);
          const employeeName = employee ? `${employee.eName} ${employee.eSurname}` : job.employeeId;
          
          jobsSheet.addRow({
            date: new Date(job.startDate).toLocaleDateString('tr-TR'),
            employeeName: employeeName,
            customerName: '',
            projectName: '',
            jobType: job.title || 'Genel',
            description: '',
            status: job.status ? 'Tamamlandı' : 'Devam Ediyor',
            duration: ''
          });
        });
      }

      // Excel dosyasını indir
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const fileName = `Detayli_Rapor_${startDate}_${endDate}.xlsx`;
      link.download = fileName;
      link.click();
      
      window.URL.revokeObjectURL(url);
      
      toast.success(`Detaylı rapor başarıyla oluşturuldu! (${filteredVisits.length} visit, ${filteredJobs.length} iş)`);
      setShowDateRangePicker(false);
    } catch (error) {
      console.error('❌ Error generating report:', error);
      toast.error('Rapor oluşturulurken hata oluştu');
    }
  };

  const handleExportPersonnelReport = async () => {
    try {
      const data = filteredPersonnelEmployees;
      if (!data || data.length === 0) {
        toast.warning('İndirilecek veri bulunamadı');
        return;
      }

      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Personel Raporları');

      sheet.columns = [
        { header: 'Çalışan', key: 'employeeName', width: 25 },
        { header: 'Ziyaret', key: 'totalVisits', width: 12 },
        { header: 'Tamamlanan Z.', key: 'completedVisits', width: 15 },
        { header: 'İş', key: 'totalJobs', width: 12 },
        { header: 'Tamamlanan İ.', key: 'completedJobs', width: 15 },
        { header: 'Proje', key: 'projectsWorkedOn', width: 12 },
        { header: 'Başarı (%)', key: 'completionRate', width: 12 }
      ];

      // Style header row
      sheet.getRow(1).font = { bold: true };
      sheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF3182CE' } };
      sheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };

      data.forEach(emp => {
        const totalTasks = emp.totalVisits + emp.totalJobs;
        const completedTasks = emp.completedVisits + emp.completedJobs;
        const rate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
        sheet.addRow({
          employeeName: emp.employeeName,
          totalVisits: emp.totalVisits,
          completedVisits: emp.completedVisits,
          totalJobs: emp.totalJobs,
          completedJobs: emp.completedJobs,
          projectsWorkedOn: emp.projectsWorkedOn,
          completionRate: rate
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Personel_Raporlari_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success('Personel raporu başarıyla indirildi!');
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Excel indirme sırasında hata oluştu');
    }
  };

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        {/* Header Section */}
        <header className={`${styles.header} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>
              <FaCalendarAlt className={styles.headerIcon} />
              Operasyon Yönetimi
            </h1>
            <div className={styles.headerButtons}>
              <button 
                className={styles.reportButton} 
                onClick={() => setShowDateRangePicker(true)}
              >
                <FaFileExcel className={styles.buttonIcon} /> Detaylı Rapor
              </button>
            </div>
          </div>
        </header>

        {/* Filter and Controls Section */}
        <div className={`${styles.controls} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.filterSection}>
            <div className={styles.employeeFilter}>
              <label htmlFor="employeeSelect" className={styles.filterLabel}>
                <FaUser className={styles.filterIcon} />
                Çalışan Filtresi:
              </label>
              <select
                id="employeeSelect"
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="">Tüm Çalışanlar</option>
                {filterableEmployees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.eName} {emp.eSurname}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.calendarControls}>
            <div className={styles.navControls}>
              <button 
                className={styles.iconButton}
                onClick={handlePrev}
              >
                <FaArrowLeft />
              </button>
              <span className={styles.calendarTitle}>
                {getNavigationTitle()}
              </span>
              <button 
                className={styles.iconButton}
                onClick={handleNext}
              >
                <FaArrowRight />
              </button>
            </div>
            
            <div className={styles.viewControls}>
              <button 
                className={`${styles.viewButton} ${viewMode === 'today' ? styles.activeView : ''}`}
                onClick={handleTodayClick}
              >
                Bugün
              </button>
              <button 
                className={`${styles.viewButton} ${viewMode === 'month' ? styles.activeView : ''}`}
                onClick={() => setViewMode('month')}
              >
                <FaCalendarAlt className={styles.viewIcon} /> Ay
              </button>
              <button 
                className={`${styles.viewButton} ${viewMode === 'week' ? styles.activeView : ''}`}
                onClick={() => setViewMode('week')}
              >
                <FaCalendarWeek className={styles.viewIcon} /> Hafta
              </button>
              <button 
                className={`${styles.viewButton} ${viewMode === 'day' ? styles.activeView : ''}`}
                onClick={() => setViewMode('day')}
              >
                <FaCalendarDay className={styles.viewIcon} /> Gün
              </button>
              <button 
                className={`${styles.viewButton} ${viewMode === 'list' ? styles.activeView : ''}`}
                onClick={() => setViewMode('list')}
              >
                <FaListUl className={styles.viewIcon} /> Liste
              </button>
            </div>
          </div>
        </div>

        {/* Calendar Section */}
        <div className={`${styles.calendarWrapper} ${isDarkMode ? 'dark-mode' : ''}`} ref={calendarRef}>
          {isLoading && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingSpinner}></div>
              <p className={styles.loadingText}>Yükleniyor...</p>
            </div>
          )}

          {!selectedEmployeeId ? (
            <div className={styles.emptyStatePlaceholder}>
              <FaUser className={styles.emptyStateIcon} />
              <h3 className={styles.emptyStateTitle}>Görüntülemek için bir çalışan seçin</h3>
              <p className={styles.emptyStateText}>
                Veriler, yukarıdaki <strong>Çalışan Filtresi</strong>'nden bir çalışan
                seçildiğinde yüklenecektir.
              </p>
            </div>
          ) : (
            renderContent()
          )}

          {/* Legend */}
          <div className={styles.legendContainer}>
            <div className={styles.legendItems}>
              <div className={styles.legendItem}>
                <div className={`${styles.legendDot} ${styles.completedDot}`}>
                  <FaCheck aria-hidden="true" />
                </div>
                <span className={styles.legendText}>Tamamlandığı gün</span>
              </div>
              <div className={styles.legendItem}>
                <div className={`${styles.legendDot} ${styles.windowClosedDot}`}></div>
                <span className={styles.legendText}>Pencere kapandı</span>
              </div>
              <div className={styles.legendItem}>
                <div className={`${styles.legendDot} ${styles.inProgressDot}`}></div>
                <span className={styles.legendText}>Devam Ediyor</span>
              </div>
              <div className={styles.legendItem}>
                <div className={`${styles.legendDot} ${styles.overdueDot}`}></div>
                <span className={styles.legendText}>Tamamlanmadı</span>
              </div>
              <div className={styles.legendItem}>
                <div className={`${styles.legendDot} ${styles.plannedDot}`}></div>
                <span className={styles.legendText}>Planlanan</span>
              </div>
            </div>
          </div>
        </div>

        {/* Personnel Reports Section */}
        <div className={`${styles.personnelReportsSection} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div 
            className={styles.personnelReportsHeader}
            onClick={() => setShowPersonnelReports(!showPersonnelReports)}
          >
            <div className={styles.personnelReportsTitle}>
              <FaChartBar className={styles.personnelReportsIcon} />
              <h2>Personel Raporları</h2>
              <span className={styles.reportBadge}>
                {allDataLoaded
                  ? `${personnelStatistics.totals.activeEmployees} Aktif Çalışan`
                  : `${employees.filter(e => e.Active === 1).length} Aktif Çalışan`}
              </span>
            </div>
            <div className={styles.personnelReportsToggle}>
              {showPersonnelReports ? <FaChevronUp /> : <FaChevronDown />}
            </div>
          </div>

          {showPersonnelReports && isLoadingAllData && !allDataLoaded && (
            <div className={styles.personnelReportsContent}>
              <div className={styles.inlineLoader}>
                <div className={styles.loadingSpinner}></div>
                <p className={styles.loadingText}>Tüm çalışanlar için veriler yükleniyor...</p>
              </div>
            </div>
          )}

          {showPersonnelReports && allDataLoaded && (
            <div className={styles.personnelReportsContent}>
              {/* Period Filter */}
              <div className={styles.reportPeriodFilter}>
                <span className={styles.periodFilterLabel}>Dönem:</span>
                <div className={styles.periodButtons}>
                  <button 
                    className={`${styles.periodButton} ${reportPeriod === 'week' ? styles.activePeriod : ''}`}
                    onClick={() => setReportPeriod('week')}
                  >
                    Bu Hafta
                  </button>
                  <button 
                    className={`${styles.periodButton} ${reportPeriod === 'month' ? styles.activePeriod : ''}`}
                    onClick={() => setReportPeriod('month')}
                  >
                    Bu Ay
                  </button>
                  <button 
                    className={`${styles.periodButton} ${reportPeriod === 'year' ? styles.activePeriod : ''}`}
                    onClick={() => setReportPeriod('year')}
                  >
                    Bu Yıl
                  </button>
                  <button 
                    className={`${styles.periodButton} ${reportPeriod === 'custom' ? styles.activePeriod : ''}`}
                    onClick={() => setReportPeriod('custom')}
                  >
                    Özel
                  </button>
                </div>
                {reportPeriod === 'custom' && (
                  <div className={styles.customDateRange}>
                    <input
                      type="date"
                      value={customReportStartDate}
                      onChange={(e) => setCustomReportStartDate(e.target.value)}
                      className={styles.customDateInput}
                    />
                    <span className={styles.dateRangeSeparator}>-</span>
                    <input
                      type="date"
                      value={customReportEndDate}
                      onChange={(e) => setCustomReportEndDate(e.target.value)}
                      className={styles.customDateInput}
                    />
                  </div>
                )}
                <button
                  className={styles.personnelExcelButton}
                  onClick={handleExportPersonnelReport}
                  title="Excel olarak indir"
                >
                  <FaFileExcel /> Excel İndir
                </button>
              </div>

              {/* Summary Cards */}
              <div className={styles.reportSummaryCards}>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryCardIcon} style={{ backgroundColor: 'rgba(49, 130, 206, 0.1)', color: '#3182ce' }}>
                    <FaClipboardList />
                  </div>
                  <div className={styles.summaryCardContent}>
                    <span className={styles.summaryCardValue}>{personnelStatistics.totals.totalVisits}</span>
                    <span className={styles.summaryCardLabel}>Toplam Ziyaret</span>
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryCardIcon} style={{ backgroundColor: 'rgba(56, 161, 105, 0.1)', color: '#38a169' }}>
                    <FaCheckCircle />
                  </div>
                  <div className={styles.summaryCardContent}>
                    <span className={styles.summaryCardValue}>{personnelStatistics.totals.completedVisits}</span>
                    <span className={styles.summaryCardLabel}>Tamamlanan Ziyaret</span>
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryCardIcon} style={{ backgroundColor: 'rgba(236, 201, 75, 0.1)', color: '#d69e2e' }}>
                    <FaHourglassHalf />
                  </div>
                  <div className={styles.summaryCardContent}>
                    <span className={styles.summaryCardValue}>{personnelStatistics.totals.inProgressVisits}</span>
                    <span className={styles.summaryCardLabel}>Devam Eden Ziyaret</span>
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryCardIcon} style={{ backgroundColor: 'rgba(99, 179, 237, 0.1)', color: '#4299e1' }}>
                    <FaCalendarAlt />
                  </div>
                  <div className={styles.summaryCardContent}>
                    <span className={styles.summaryCardValue}>{personnelStatistics.totals.totalJobs}</span>
                    <span className={styles.summaryCardLabel}>Toplam İş</span>
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryCardIcon} style={{ backgroundColor: 'rgba(72, 187, 120, 0.1)', color: '#48bb78' }}>
                    <FaCheckCircle />
                  </div>
                  <div className={styles.summaryCardContent}>
                    <span className={styles.summaryCardValue}>{personnelStatistics.totals.completedJobs}</span>
                    <span className={styles.summaryCardLabel}>Tamamlanan İş</span>
                  </div>
                </div>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryCardIcon} style={{ backgroundColor: 'rgba(144, 97, 249, 0.1)', color: '#805ad5' }}>
                    <FaProjectDiagram />
                  </div>
                  <div className={styles.summaryCardContent}>
                    <span className={styles.summaryCardValue}>{personnelStatistics.totals.uniqueProjects}</span>
                    <span className={styles.summaryCardLabel}>Proje</span>
                  </div>
                </div>
              </div>

              {/* Top Performers Section */}
              {personnelStatistics.topByVisits.length > 0 && (
                <div className={styles.topPerformersSection}>
                  <h3 className={styles.topPerformersTitle}>
                    <FaTrophy className={styles.trophyIcon} />
                    En Çok Ziyaret Yapanlar
                  </h3>
                  <div className={styles.topPerformersGrid}>
                    {personnelStatistics.topByVisits.map((performer, index) => (
                      <div key={performer.employeeId} className={styles.topPerformerCard}>
                        <div className={`${styles.topPerformerRank} ${styles[`rank${index + 1}`]}`}>
                          {index === 0 && <FaTrophy />}
                          {index === 1 && <FaMedal />}
                          {index === 2 && <FaStar />}
                        </div>
                        <div className={styles.topPerformerInfo}>
                          <span className={styles.topPerformerName}>{performer.employeeName}</span>
                          <div className={styles.topPerformerStats}>
                            <span className={styles.topPerformerVisits}>{performer.totalVisits} ziyaret</span>
                            <span className={styles.topPerformerRate}>{performer.completionRate}% tamamlama</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Employee Statistics Table */}
              <div className={styles.employeeStatsTable}>
                <div className={styles.tableHeader}>
                  <div 
                    className={`${styles.tableHeaderCell} ${styles.sortableHeader}`} 
                    style={{ flex: 2 }}
                    onClick={() => handleSort('employeeName')}
                  >
                    <span>Çalışan</span>
                    <span className={styles.sortIcon}>
                      {sortField === 'employeeName' ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort />}
                    </span>
                  </div>
                  <div 
                    className={`${styles.tableHeaderCell} ${styles.sortableHeader}`}
                    onClick={() => handleSort('totalVisits')}
                  >
                    <span>Ziyaret</span>
                    <span className={styles.sortIcon}>
                      {sortField === 'totalVisits' ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort />}
                    </span>
                  </div>
                  <div 
                    className={`${styles.tableHeaderCell} ${styles.sortableHeader}`}
                    onClick={() => handleSort('completedVisits')}
                  >
                    <span>Tamamlanan Z.</span>
                    <span className={styles.sortIcon}>
                      {sortField === 'completedVisits' ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort />}
                    </span>
                  </div>
                  <div 
                    className={`${styles.tableHeaderCell} ${styles.sortableHeader}`}
                    onClick={() => handleSort('totalJobs')}
                  >
                    <span>İş</span>
                    <span className={styles.sortIcon}>
                      {sortField === 'totalJobs' ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort />}
                    </span>
                  </div>
                  <div 
                    className={`${styles.tableHeaderCell} ${styles.sortableHeader}`}
                    onClick={() => handleSort('completedJobs')}
                  >
                    <span>Tamamlanan İ.</span>
                    <span className={styles.sortIcon}>
                      {sortField === 'completedJobs' ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort />}
                    </span>
                  </div>
                  <div 
                    className={`${styles.tableHeaderCell} ${styles.sortableHeader}`}
                    onClick={() => handleSort('projectsWorkedOn')}
                  >
                    <span>Proje</span>
                    <span className={styles.sortIcon}>
                      {sortField === 'projectsWorkedOn' ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort />}
                    </span>
                  </div>
                  <div 
                    className={`${styles.tableHeaderCell} ${styles.sortableHeader}`}
                    onClick={() => handleSort('completionRate')}
                  >
                    <span>Başarı</span>
                    <span className={styles.sortIcon}>
                      {sortField === 'completionRate' ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort />}
                    </span>
                  </div>
                </div>
                <div className={styles.tableFilterRow}>
                  <div className={styles.tableFilterCell} style={{ flex: 2 }}>
                    <input
                      type="text"
                      className={styles.columnFilterInput}
                      placeholder="İsim ara..."
                      value={reportFilters.employeeName}
                      onChange={(e) => handleReportFilterChange('employeeName', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className={styles.tableFilterCell}>
                    <input
                      type="text"
                      className={styles.columnFilterInput}
                      placeholder="Örn: >5"
                      value={reportFilters.totalVisits}
                      onChange={(e) => handleReportFilterChange('totalVisits', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className={styles.tableFilterCell}>
                    <input
                      type="text"
                      className={styles.columnFilterInput}
                      placeholder="Örn: >3"
                      value={reportFilters.completedVisits}
                      onChange={(e) => handleReportFilterChange('completedVisits', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className={styles.tableFilterCell}>
                    <input
                      type="text"
                      className={styles.columnFilterInput}
                      placeholder="Örn: >0"
                      value={reportFilters.totalJobs}
                      onChange={(e) => handleReportFilterChange('totalJobs', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className={styles.tableFilterCell}>
                    <input
                      type="text"
                      className={styles.columnFilterInput}
                      placeholder="Örn: >=1"
                      value={reportFilters.completedJobs}
                      onChange={(e) => handleReportFilterChange('completedJobs', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className={styles.tableFilterCell}>
                    <input
                      type="text"
                      className={styles.columnFilterInput}
                      placeholder="Örn: >2"
                      value={reportFilters.projectsWorkedOn}
                      onChange={(e) => handleReportFilterChange('projectsWorkedOn', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className={styles.tableFilterCell}>
                    <input
                      type="text"
                      className={styles.columnFilterInput}
                      placeholder="Örn: >50"
                      value={reportFilters.completionRate}
                      onChange={(e) => handleReportFilterChange('completionRate', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
                <div className={styles.tableBody}>
                  {filteredPersonnelEmployees.length === 0 ? (
                    <div className={styles.emptyTableState}>
                      <FaUserTie className={styles.emptyTableIcon} />
                      <span>Bu dönemde kayıt bulunan çalışan yok</span>
                    </div>
                  ) : (
                    filteredPersonnelEmployees.map((stat, index) => (
                      <div 
                        key={stat.employeeId} 
                        className={`${styles.tableRow} ${styles.clickableRow} ${index % 2 === 0 ? styles.tableRowEven : ''}`}
                        onClick={() => handleEmployeeClick(stat)}
                      >
                        <div className={styles.tableCell} style={{ flex: 2 }}>
                          <div className={styles.employeeCell}>
                            <div className={styles.employeeAvatar}>
                              {stat.employeeName.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            </div>
                            <span className={styles.employeeName}>{stat.employeeName}</span>
                          </div>
                        </div>
                        <span className={styles.tableCell}>
                          <span className={styles.statValue}>{stat.totalVisits}</span>
                        </span>
                        <span className={styles.tableCell}>
                          <span className={`${styles.statBadge} ${styles.completedBadge}`}>{stat.completedVisits}</span>
                        </span>
                        <span className={styles.tableCell}>
                          <span className={styles.statValue}>{stat.totalJobs}</span>
                        </span>
                        <span className={styles.tableCell}>
                          <span className={`${styles.statBadge} ${styles.completedBadge}`}>{stat.completedJobs}</span>
                        </span>
                        <span className={styles.tableCell}>
                          <span className={styles.statValue}>{stat.projectsWorkedOn}</span>
                        </span>
                        <span className={styles.tableCell}>
                          <div className={styles.progressBarContainer}>
                            <div 
                              className={styles.progressBar} 
                              style={{ 
                                width: `${stat.completionRate}%`,
                                backgroundColor: stat.completionRate >= 80 ? '#38a169' : stat.completionRate >= 50 ? '#d69e2e' : '#e53e3e'
                              }}
                            ></div>
                            <span className={styles.progressLabel}>{stat.completionRate}%</span>
                          </div>
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* SC Tracking Table */}
        <SCTrackingTable />
      </div>

      {/* Tooltip */}
      {showTooltip && (
        <div 
          className={styles.tooltip} 
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`
          }}
        >
          {tooltipContent}
        </div>
      )}

      {/* Date range picker modal */}
      {showDateRangePicker && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modalContent} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Rapor Tarih Aralığı Seçin</h3>
              <button 
                className={styles.closeButton} 
                onClick={() => setShowDateRangePicker(false)}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className={styles.datePickerContainer}>
              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>Başlangıç Tarihi:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={styles.dateInput}
                />
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>Bitiş Tarihi:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  className={styles.dateInput}
                />
              </div>
            </div>
            
            <div className={styles.modalButtons}>
              <button 
                className={styles.confirmButton}
                onClick={handleGenerateReport}
              >
                Rapor Oluştur
              </button>
              <button 
                className={styles.cancelButton} 
                onClick={() => setShowDateRangePicker(false)}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visit Modal */}
      <VisitModal
        show={showVisitModal}
        onHide={() => setShowVisitModal(false)}
        visit={selectedVisit}
        onVisitUpdate={handleVisitUpdate}
        onCascadeDelete={isAdmin ? (visit) => buildCascadePreview(visit, 'visit') : undefined}
      />

      {/* Job Details Modal */}
      {showJobDeleteModal && selectedJob && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modalContent} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>İş Detayları</h3>
              <button
                className={styles.closeButton}
                onClick={() => {
                  setShowJobDeleteModal(false);
                  setSelectedJob(null);
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p><strong>İş:</strong> {selectedJob.title}</p>
              <p><strong>Çalışan:</strong> {getEmployeeName(selectedJob.employeeId) || 'Belirtilmemiş'}</p>
              <p><strong>Başlangıç:</strong> {new Date(selectedJob.startDate).toLocaleDateString('tr-TR')}</p>
              <p><strong>Bitiş:</strong> {new Date(selectedJob.endDate).toLocaleDateString('tr-TR')}</p>
              {selectedJob.time && (
                <p><strong>Saat:</strong> {selectedJob.time}</p>
              )}
              {selectedJob.description && (
                <p><strong>Açıklama:</strong> {selectedJob.description}</p>
              )}
              <p style={{ marginTop: '12px' }}>
                <strong>Durum: </strong>
                <span
                  className={`${styles.jobStatusBadge} ${selectedJob.status ? styles.jobStatusCompleted : styles.jobStatusPending}`}
                >
                  {selectedJob.status ? '✓ Tamamlandı' : '○ Devam Ediyor'}
                </span>
              </p>
            </div>

            <div className={styles.modalButtons}>
              <button
                className={selectedJob.status ? styles.markIncompleteBtn : styles.markCompleteBtn}
                onClick={handleToggleJobStatus}
              >
                {selectedJob.status ? 'Tamamlanmadı Olarak İşaretle' : '✓ Tamamlandı Olarak İşaretle'}
              </button>
              {isRestrictedUser && (
                <button
                  className={styles.confirmButton}
                  onClick={handleDeleteJob}
                  disabled={isDeletingJob}
                  style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                >
                  {isDeletingJob ? 'Siliniyor...' : 'Sil'}
                </button>
              )}
              {isAdmin && (
                <button
                  className={styles.confirmButton}
                  onClick={() => buildCascadePreview(selectedJob, 'job')}
                  disabled={isBuildingCascade}
                  style={{ backgroundColor: '#a71d2a', borderColor: '#a71d2a' }}
                >
                  Bu ve Sonraki İşleri Sil
                </button>
              )}
              <button
                className={styles.cancelButton}
                onClick={() => {
                  setShowJobDeleteModal(false);
                  setSelectedJob(null);
                }}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cascade Delete Preview Modal — admin only */}
      {isAdmin && cascade && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modalContent} ${styles.cascadeModal} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {cascade.type === 'visit' ? 'Ziyaret Serisini Sil' : 'İş Serisini Sil'}
              </h3>
              <button
                className={styles.closeButton}
                onClick={() => setCascade(null)}
                disabled={isCascadeDeleting}
              >
                <FaTimes />
              </button>
            </div>

            <div className={styles.cascadeBody}>
              <div className={styles.cascadeWarning}>
                <FaInfoCircle className={styles.cascadeWarningIcon} />
                <span>
                  Aşağıdaki kayıtlar <strong>kalıcı olarak</strong> silinecek. Bu işlem geri alınamaz.
                </span>
              </div>

              <div className={styles.cascadeCriteria}>
                <p>
                  <strong>Çalışan:</strong> {getEmployeeName(cascade.anchor.employeeId) || 'Belirtilmemiş'}
                </p>
                <p>
                  <strong>{cascade.type === 'visit' ? 'Hasta ID' : 'İş Başlığı'}:</strong> {cascade.seriesKey}
                </p>
                <p>
                  <strong>Başlangıç tarihi:</strong> {cascade.anchorDate.toLocaleDateString('tr-TR')} ve sonrası
                </p>
              </div>

              {isBuildingCascade ? (
                <div className={styles.cascadeLoading}>Kayıtlar taranıyor...</div>
              ) : cascade.items.length === 0 ? (
                <div className={styles.cascadeLoading}>Silinecek kayıt bulunamadı.</div>
              ) : (
                <>
                  <div className={styles.cascadeCount}>
                    Silinecek kayıt sayısı: <strong>{cascade.items.length}</strong>
                  </div>
                  <div className={styles.cascadeList}>
                    {cascade.items.map((record) => {
                      const isAnchor = record.id === cascade.anchor.id;
                      const recordDate = cascade.type === 'visit'
                        ? new Date(record.date || record.firstVisitDate)
                        : new Date(record.startDate);
                      return (
                        <div
                          key={record.id}
                          className={`${styles.cascadeItem} ${isAnchor ? styles.cascadeItemAnchor : ''}`}
                        >
                          <div className={styles.cascadeItemDate}>
                            {recordDate.toLocaleDateString('tr-TR')}
                          </div>
                          <div className={styles.cascadeItemMain}>
                            <div className={styles.cascadeItemTitle}>
                              {cascade.type === 'visit'
                                ? (getProjectName(record.projectId) || 'Proje Belirtilmemiş')
                                : record.title}
                              {isAnchor && <span className={styles.cascadeItemBadge}>Seçilen</span>}
                            </div>
                            <div className={styles.cascadeItemMeta}>
                              {cascade.type === 'visit' ? (
                                <>
                                  {record.visitType && <span>{record.visitType}</span>}
                                  {record.visitNumber && <span>Ziyaret No: {record.visitNumber}</span>}
                                  <span>{record.period || 1} gün</span>
                                </>
                              ) : (
                                <span>
                                  {new Date(record.startDate).toLocaleDateString('tr-TR')} –{' '}
                                  {record.endDate ? new Date(record.endDate).toLocaleDateString('tr-TR') : '?'}
                                </span>
                              )}
                              <span className={record.status ? styles.cascadeDone : styles.cascadePending}>
                                {record.status ? 'Tamamlandı' : 'Devam Ediyor'}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <div className={styles.modalButtons}>
              <button
                className={styles.cancelButton}
                onClick={() => setCascade(null)}
                disabled={isCascadeDeleting}
              >
                Vazgeç
              </button>
              <button
                className={styles.confirmButton}
                onClick={handleConfirmCascadeDelete}
                disabled={isCascadeDeleting || isBuildingCascade || cascade.items.length === 0}
                style={{ backgroundColor: '#a71d2a', borderColor: '#a71d2a' }}
              >
                {isCascadeDeleting
                  ? 'Siliniyor...'
                  : `${cascade.items.length} Kaydı Kalıcı Olarak Sil`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Employee Details Modal */}
      {showEmployeeModal && selectedEmployeeStats && (
        <div className={styles.modalOverlay} onClick={() => setShowEmployeeModal(false)}>
          <div 
            className={`${styles.employeeModalContent} ${isDarkMode ? 'dark-mode' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.employeeModalHeader}>
              <div className={styles.employeeModalProfile}>
                <div className={styles.employeeModalAvatar}>
                  {selectedEmployeeStats.employeeName.split(' ').map(n => n[0]).join('').substring(0, 2)}
                </div>
                <div className={styles.employeeModalInfo}>
                  <h2 className={styles.employeeModalName}>{selectedEmployeeStats.employeeName}</h2>
                  <span className={styles.employeeModalPeriod}>
                    {personnelStatistics.filterStartDate.toLocaleDateString('tr-TR')} - {personnelStatistics.filterEndDate.toLocaleDateString('tr-TR')}
                  </span>
                </div>
              </div>
              <button
                className={styles.closeButton}
                onClick={() => setShowEmployeeModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            {/* Stats Summary */}
            <div className={styles.employeeModalStats}>
              <div className={styles.employeeStatItem}>
                <span className={styles.employeeStatValue}>{selectedEmployeeStats.totalVisits}</span>
                <span className={styles.employeeStatLabel}>Toplam Ziyaret</span>
              </div>
              <div className={styles.employeeStatItem}>
                <span className={styles.employeeStatValue} style={{ color: '#38a169' }}>{selectedEmployeeStats.completedVisits}</span>
                <span className={styles.employeeStatLabel}>Tamamlanan Z.</span>
              </div>
              <div className={styles.employeeStatItem}>
                <span className={styles.employeeStatValue} style={{ color: '#4299e1' }}>{selectedEmployeeStats.totalJobs}</span>
                <span className={styles.employeeStatLabel}>Toplam İş</span>
              </div>
              <div className={styles.employeeStatItem}>
                <span className={styles.employeeStatValue} style={{ color: '#48bb78' }}>{selectedEmployeeStats.completedJobs}</span>
                <span className={styles.employeeStatLabel}>Tamamlanan İ.</span>
              </div>
              <div className={styles.employeeStatItem}>
                <span className={styles.employeeStatValue} style={{ color: '#805ad5' }}>{selectedEmployeeStats.projectsWorkedOn}</span>
                <span className={styles.employeeStatLabel}>Proje</span>
              </div>
              <div className={styles.employeeStatItem}>
                <div className={styles.employeeStatProgress}>
                  <div 
                    className={styles.employeeStatProgressBar}
                    style={{ 
                      width: `${selectedEmployeeStats.completionRate}%`,
                      backgroundColor: selectedEmployeeStats.completionRate >= 80 ? '#38a169' : selectedEmployeeStats.completionRate >= 50 ? '#d69e2e' : '#e53e3e'
                    }}
                  ></div>
                </div>
                <span className={styles.employeeStatLabel}>{selectedEmployeeStats.completionRate}% Başarı</span>
              </div>
            </div>

            {/* Visits List */}
            <div className={styles.employeeModalVisitsSection}>
              <h3 className={styles.employeeModalVisitsTitle}>
                <FaClipboardList /> Ziyaret Detayları ({selectedEmployeeStats.visits.length})
              </h3>
              <div className={styles.employeeModalVisitsList}>
                {selectedEmployeeStats.visits.length === 0 ? (
                  <div className={styles.noVisitsMessage}>
                    <FaCalendarAlt />
                    <span>Bu dönemde ziyaret bulunmuyor</span>
                  </div>
                ) : (
                  selectedEmployeeStats.visits.map((visit, index) => (
                    <div 
                      key={visit.id} 
                      className={`${styles.visitListItem} ${visit.status ? styles.visitItemCompleted : styles.visitItemInProgress}`}
                    >
                      <div className={styles.visitListItemMain}>
                        <div className={styles.visitListItemDate}>
                          <span className={styles.visitDateDay}>
                            {new Date(visit.date || visit.firstVisitDate).getDate()}
                          </span>
                          <span className={styles.visitDateMonth}>
                            {new Date(visit.date || visit.firstVisitDate).toLocaleDateString('tr-TR', { month: 'short' })}
                          </span>
                        </div>
                        <div className={styles.visitListItemContent}>
                          <div className={styles.visitListItemTop}>
                            <span className={styles.visitListItemProject}>
                              {getProjectName(visit.projectId) || 'Proje Belirtilmemiş'}
                            </span>
                            <span className={`${styles.visitListItemStatus} ${visit.status ? styles.statusDone : styles.statusPending}`}>
                              {visit.status ? 'Tamamlandı' : 'Devam Ediyor'}
                            </span>
                          </div>
                          <div className={styles.visitListItemMeta}>
                            <span className={styles.visitMetaItem}>
                              <FaCalendarCheck /> {visit.period || 1} Gün
                            </span>
                            {visit.visitType && (
                              <span className={styles.visitMetaItem}>
                                <FaInfoCircle /> {visit.visitType}
                              </span>
                            )}
                            {visit.patientId && (
                              <span className={styles.visitMetaItem}>
                                <FaUser /> {visit.patientId}
                              </span>
                            )}
                          </div>
                          {visit.info && (
                            <p className={styles.visitListItemNote}>{visit.info}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Jobs List */}
            <div className={styles.employeeModalVisitsSection}>
              <h3 className={styles.employeeModalVisitsTitle}>
                <FaCalendarAlt /> İş Detayları ({selectedEmployeeStats.jobs?.length || 0})
              </h3>
              <div className={styles.employeeModalVisitsList}>
                {(!selectedEmployeeStats.jobs || selectedEmployeeStats.jobs.length === 0) ? (
                  <div className={styles.noVisitsMessage}>
                    <FaCalendarAlt />
                    <span>Bu dönemde iş bulunmuyor</span>
                  </div>
                ) : (
                  selectedEmployeeStats.jobs.map((job) => (
                    <div 
                      key={job.id} 
                      className={`${styles.visitListItem} ${job.status ? styles.visitItemCompleted : styles.visitItemInProgress}`}
                    >
                      <div className={styles.visitListItemMain}>
                        <div className={styles.visitListItemDate}>
                          <span className={styles.visitDateDay}>
                            {new Date(job.startDate).getDate()}
                          </span>
                          <span className={styles.visitDateMonth}>
                            {new Date(job.startDate).toLocaleDateString('tr-TR', { month: 'short' })}
                          </span>
                        </div>
                        <div className={styles.visitListItemContent}>
                          <div className={styles.visitListItemTop}>
                            <span className={styles.visitListItemProject}>
                              {job.title}
                            </span>
                            <span className={`${styles.visitListItemStatus} ${job.status ? styles.statusDone : styles.statusPending}`}>
                              {job.status ? 'Tamamlandı' : 'Devam Ediyor'}
                            </span>
                          </div>
                          <div className={styles.visitListItemMeta}>
                            <span className={styles.visitMetaItem}>
                              <FaCalendarAlt /> {new Date(job.startDate).toLocaleDateString('tr-TR')} - {new Date(job.endDate).toLocaleDateString('tr-TR')}
                            </span>
                            {job.time && (
                              <span className={styles.visitMetaItem}>
                                <FaInfoCircle /> {job.time}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className={styles.employeeModalFooter}>
              <button 
                className={styles.closeModalButton}
                onClick={() => setShowEmployeeModal(false)}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationManagementPage;
